import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { useWaitlist } from '../../../contexts/WaitlistContext';

const BetaActionsSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { addToWaitlist } = useWaitlist();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(val?.trim());

  const handleOpenModal = () => {
    setShowEmailModal(true);
    setEmail('');
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});
    if (!email?.trim()) { setErrors({ email: 'Email is required' }); return; }
    if (!validateEmail(email)) { setErrors({ email: 'Please enter a valid email address' }); return; }
    setIsSubmitting(true);
    try {
      const result = await addToWaitlist(email);
      if (result?.success) {
        setEmail('');
        setShowEmailModal(false);
        setShowSuccessModal(true);
      } else {
        setErrors({ submit: result?.message || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setErrors({ submit: error?.message || 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToFeedback = () => {
    document.getElementById('feedback-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const actions = [
    {
      icon: 'Sparkles',
      title: 'Try DepthMe',
      description: 'Experience AI-guided journaling and start your transformation.',
      buttonLabel: 'Get Early Access',
      gradient: 'from-purple-500/20 to-blue-500/20',
      onClick: handleOpenModal
    },
    {
      icon: 'MessageSquare',
      title: 'Give Feedback',
      description: 'Tell us what feels confusing, helpful, or missing.',
      buttonLabel: 'Share Feedback',
      gradient: 'from-blue-500/20 to-cyan-500/20',
      onClick: scrollToFeedback
    },
    {
      icon: 'ExternalLink',
      title: 'Open App',
      description: 'Go directly to DepthMe and start your journey.',
      buttonLabel: 'Open App',
      gradient: 'from-purple-500/20 to-blue-500/20',
      onClick: () => window.open('https://depthme.app', '_blank', 'noopener,noreferrer')
    }
  ];

  return (
    <section id="beta-actions" ref={sectionRef} className="relative py-12 px-6 overflow-hidden">
      {/* image2.jpg subtle bg */}
      <div className="absolute inset-0 pointer-events-none">
        <img src="/image2.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-background/30" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto">
        <div className={`text-center mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            What you can do right now
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {actions?.map((action) => (
            <div
              key={action?.title}
              className="group relative glassmorphism p-6 rounded-xl hover-lift transition-all duration-300 flex flex-col"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${action?.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl`} />
              <div className="relative z-10 flex flex-col flex-1">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${action?.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={action?.icon} size={20} className="text-foreground" />
                </div>
                <h3 className="text-base font-bold text-foreground mb-2">{action?.title}</h3>
                <p className="text-sm text-muted-foreground mb-5 flex-1">{action?.description}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={action?.onClick}
                  className="w-full"
                >
                  {action?.buttonLabel}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glassmorphism p-6 rounded-2xl max-w-md w-full">
            <div className="text-center mb-6">
              <Icon name="Sparkles" size={40} className="text-primary mx-auto mb-3" />
              <h3 className="text-xl font-bold text-foreground mb-2">Join the Beta</h3>
              <p className="text-sm text-muted-foreground">Enter your email to get access</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                label="Email Address"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                error={errors?.email}
                required
              />
              {errors?.submit && (
                <div className="text-error text-sm text-center">{errors?.submit}</div>
              )}
              <div className="flex space-x-3">
                <Button type="button" onClick={() => setShowEmailModal(false)} variant="outline" size="lg" className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="default" size="lg" loading={isSubmitting} className="flex-1 violet-glow" iconName="ArrowRight" iconPosition="right">
                  {isSubmitting ? 'Joining...' : 'Get Access'}
                </Button>
              </div>
              <div className="text-center text-xs text-muted-foreground flex items-center justify-center space-x-2">
                <Icon name="Shield" size={12} className="text-accent" />
                <span>Your privacy is protected. Unsubscribe anytime.</span>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glassmorphism p-8 rounded-2xl max-w-lg w-full text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
              <Icon name="CheckCircle" size={32} className="text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">You're in! 🎉</h2>
            <p className="text-base text-muted-foreground mb-3">
              Check your email for beta access details.
            </p>
            <p className="text-sm text-muted-foreground opacity-60 mb-6">
              If you don't see it, check your Spam folder and mark it as 'Not spam' 🌙
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-accent mb-6">
              <Icon name="Mail" size={16} />
              <span>Confirmation sent</span>
            </div>
            <Button onClick={() => setShowSuccessModal(false)} variant="default" size="lg" className="violet-glow">
              Continue
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default BetaActionsSection;
